import { Component } from '@theme/component'
import { loadGsapCore } from '@theme/gsap-runtime'

const SearchTypedConfig = {
  attributes: {
    delay: 'data-delay',
    firstText: 'data-first-text',
    lastText: 'data-last-text'
  },
  classes: {
    blink: 'blink',
    cursor: 'typed-cursor'
  },
  events: {
    dialogClose: 'dialog:close',
    dialogOpen: 'dialog:open'
  },
  selectors: {
    dialogHost: 'use-dialog'
  },
  timings: {
    swapMilliseconds: 600,
    typeInSeconds: 1,
    typeOutSeconds: 0.25
  }
}

class SearchTyped extends Component {
  connectedCallback() {
    super.connectedCallback()
    this.initializeRuntimeState()
    this.connectSearchTrigger()
  }

  initializeRuntimeState() {
    this.handleIntersect = this.handleIntersect.bind(this)
    this.handleDialogOpen = this.handleDialogOpen.bind(this)
    this.handleDialogClose = this.handleDialogClose.bind(this)
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.dialogHost = this.closest(SearchTypedConfig.selectors.dialogHost)
    this.isRunning = false
    this.sequenceRequestId = 0
  }

  connectSearchTrigger() {
    if (this.dialogHost) {
      this.bindDialogHost()
      return
    }

    this.observeVisibility()
  }

  bindDialogHost() {
    this.dialogHost.addEventListener(
      SearchTypedConfig.events.dialogOpen,
      this.handleDialogOpen
    )
    this.dialogHost.addEventListener(
      SearchTypedConfig.events.dialogClose,
      this.handleDialogClose
    )
  }

  observeVisibility() {
    this.intersectionObserver?.disconnect()
    this.intersectionObserver = new IntersectionObserver(this.handleIntersect)
    this.intersectionObserver.observe(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.intersectionObserver?.disconnect()
    this.intersectionObserver = null
    this.dialogHost?.removeEventListener(
      SearchTypedConfig.events.dialogOpen,
      this.handleDialogOpen
    )
    this.dialogHost?.removeEventListener(
      SearchTypedConfig.events.dialogClose,
      this.handleDialogClose
    )
    this.resetCursor()
  }

  handleIntersect(entries) {
    if (!entries[0]?.isIntersecting) return
    this.intersectionObserver.disconnect()
    this.startSequence()
  }

  handleDialogOpen() {
    this.startSequence()
  }

  handleDialogClose() {
    this.sequenceRequestId += 1
    this.resetCursor()
    this.textContent = ''
    this.style.width = '0px'
    this.isRunning = false
  }

  get startDelay() {
    const raw = this.getAttribute(SearchTypedConfig.attributes.delay)
    return raw ? Number.parseFloat(raw) : 0
  }

  get firstText() {
    return this.getAttribute(SearchTypedConfig.attributes.firstText) ?? ''
  }

  get lastText() {
    return this.getAttribute(SearchTypedConfig.attributes.lastText) ?? ''
  }

  async startSequence() {
    if (this.isRunning) return
    const requestId = this.beginSequence()

    if (this.reducedMotionQuery.matches) {
      this.finishStaticSequence()
      return
    }

    const gsapRuntime = await loadGsapCore()
    if (!this.prepareSequenceRuntime(requestId, gsapRuntime)) return

    await this.playTypedSequence()
  }

  beginSequence() {
    this.isRunning = true
    return this.nextSequenceRequestId()
  }

  finishStaticSequence() {
    this.textContent = this.lastText
    this.isRunning = false
  }

  prepareSequenceRuntime(requestId, gsapRuntime) {
    if (!this.isCurrentSequence(requestId)) {
      this.isRunning = false
      return false
    }
    if (!gsapRuntime) {
      this.finishStaticSequence()
      return false
    }

    this.gsap = gsapRuntime
    this.insertCursor()
    return true
  }

  async playTypedSequence() {
    try {
      await this.typeIn(this.firstText, this.startDelay)
      await wait(SearchTypedConfig.timings.swapMilliseconds)
      await this.eraseOut()
      await this.typeIn(this.lastText, 0)
    } finally {
      this.isRunning = false
    }
  }

  nextSequenceRequestId() {
    this.sequenceRequestId += 1
    return this.sequenceRequestId
  }

  isCurrentSequence(requestId) {
    if (!this.isConnected) return false
    if (requestId !== this.sequenceRequestId) return false

    return true
  }

  async typeIn(text, delay) {
    this.innerHTML = text
    this.style.overflow = 'hidden'
    this.style.display = 'flex'
    this.style.width = '0px'

    await new Promise((resolve) => {
      this.gsap.to(this, {
        delay,
        duration: SearchTypedConfig.timings.typeInSeconds,
        ease: 'power2.out',
        onComplete: resolve,
        width: `${this.scrollWidth}px`
      })
    })

    this.cursor?.classList.add(SearchTypedConfig.classes.blink)
  }

  async eraseOut() {
    this.cursor?.classList.remove(SearchTypedConfig.classes.blink)

    await new Promise((resolve) => {
      this.gsap.to(this, {
        duration: SearchTypedConfig.timings.typeOutSeconds,
        ease: 'power2.in',
        onComplete: resolve,
        width: 0
      })
    })
  }

  insertCursor() {
    if (this.cursor || !this.parentElement) return
    this.cursor = document.createElement('span')
    this.cursor.className = SearchTypedConfig.classes.cursor
    this.cursor.setAttribute('aria-hidden', 'true')
    this.cursor.textContent = '|'
    this.parentElement.insertBefore(this.cursor, this.nextSibling)
  }

  resetCursor() {
    this.cursor?.remove()
    this.cursor = null
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

if (!customElements.get('search-typed')) {
  customElements.define('search-typed', SearchTyped)
}
